/**
 * 런타임 설정 — `GET /v1/bloom/config` (`docs/13` §1.3).
 *
 * **앱은 모드·키를 빌드 시점이 아니라 여기서 받는다** (`docs/12` §2.4). 값이 두 곳에
 * 있으면 어긋나고 어긋난 증상이 조용하기 때문이다 — VAPID 공개키가 실제로 그랬다
 * (서버와 번들에 각각 있어, 어긋나면 **권한은 허용되는데 구독만 조용히 실패한다**).
 *
 * **`config.ts` 를 import 하지 않는다.** 그 파일은 모듈 스코프에서 `window.location` 을
 * 읽으므로 끌어오면 node 환경 테스트가 통째로 죽는다 (`format.ts` 가 같은 이유로 갈려
 * 있다). 서버 주소와 override 는 호출부가 넘긴다.
 */

import type { BloomRuntimeConfig } from '@apm/contracts';

/** 쿼리로 덮을 수 있는 것들. 나머지는 배포 정책이라 브라우저가 뒤집을 수 없다 */
export interface RuntimeOverrides {
  tv2mServerUrl?: string | null;
  tv2mApiKey?: string | null;
  vapidPublicKey?: string | null;
}

/**
 * 쿼리 override 를 얹는다.
 *
 * 빈 문자열은 **값이 아니다** — `?vapid=` 로 들어오면 「비우라」가 아니라 「안 줬다」로
 * 읽는다. 비우는 것이 필요한 상황이 없고, 값으로 읽으면 오타 하나가 구독을 조용히 끊는다.
 */
export function applyRuntimeOverrides(
  config: BloomRuntimeConfig,
  overrides: RuntimeOverrides,
): BloomRuntimeConfig {
  const next = { ...config };
  if (overrides.tv2mServerUrl) next.tv2mServerUrl = overrides.tv2mServerUrl;
  if (overrides.tv2mApiKey) next.tv2mApiKey = overrides.tv2mApiKey;
  if (overrides.vapidPublicKey) next.vapidPublicKey = overrides.vapidPublicKey;
  return next;
}

/**
 * 응답이 계약의 모양인지 본다.
 *
 * **모양을 확인하지 않으면 실패가 한참 뒤로 밀린다.** 프록시가 로그인 페이지를 200 으로
 * 돌려주거나 오리진을 잘못 잡으면 `mode` 가 `undefined` 인 객체를 그대로 들고 다니게 되고,
 * 그 증상은 「알림이 안 온다」 하나다. 여기서 던지면 부팅 화면이 이유를 말한다.
 */
export function parseRuntimeConfig(value: unknown): BloomRuntimeConfig {
  if (typeof value !== 'object' || value === null) {
    throw new Error('런타임 설정 응답이 객체가 아니다.');
  }
  const raw = value as Record<string, unknown>;
  for (const key of ['mode', 'vapidPublicKey', 'tv2mServerUrl', 'tv2mApiKey', 'bloomServerUrl']) {
    if (typeof raw[key] !== 'string') {
      throw new Error(`런타임 설정에 ${key} 가 없다 — /v1/bloom/config 응답이 아니다.`);
    }
  }
  if (typeof raw['requireLogin'] !== 'boolean') {
    throw new Error('런타임 설정에 requireLogin 이 없다 — /v1/bloom/config 응답이 아니다.');
  }
  return raw as unknown as BloomRuntimeConfig;
}

/**
 * 설정 요청의 상한(ms).
 *
 * **없으면 앱이 「준비 중」에서 영영 멈춘다.** `fetch` 는 스스로 포기하지 않고 OS 타임아웃
 * (모바일에서 60초 이상)까지 매달리는데, 이 요청은 **다른 모든 것보다 먼저이고**
 * (`docs/14` B8) 실패해야 화면이 이유를 말한다 — 매달려 있으면 그 화면조차 없다.
 *
 * 2026-08-12 실기: 서버를 잠깐 내렸다 올리는 사이에 열려 있던 Android 가 그 상태였다.
 * 서버 로그에는 그 폰의 요청이 **한 줄도 없고** 화면은 「Getting ready…」뿐이라,
 * **앱의 결함처럼 보인다.**
 *
 * 5초는 네트워크 관례(3초)보다 넉넉하다 — 이 요청 하나에 부팅 전체가 걸려 있어
 * 느린 망에서 성급히 포기하면 「서버가 죽었다」는 거짓 안내가 뜬다.
 */
const CONFIG_TIMEOUT_MS = 5000;

/**
 * 서버에서 받아 override 를 얹는다.
 *
 * **실패하면 던진다.** 폴백 기본값을 두지 않는다 — 그것이 곧 「값이 두 곳에 있는」 상태이고,
 * 폴백으로 뜬 앱은 잘못된 VAPID 키로 구독을 만들어 놓고 멀쩡해 보인다. 뜨지 않는 편이 낫다.
 *
 * **다만 반드시 끝난다.** 던지는 것과 안 끝나는 것은 다르다 — 앞엣것은 화면이 이유를
 * 말하고 [다시 시도] 를 주지만, 뒤엣것은 사용자가 할 수 있는 일이 없다.
 */
export async function fetchRuntimeConfig(
  serverUrl: string,
  overrides: RuntimeOverrides = {},
): Promise<BloomRuntimeConfig> {
  const response = await fetchWithTimeout(`${serverUrl.replace(/\/+$/, '')}/v1/bloom/config`);
  if (!response.ok) {
    throw new Error(`런타임 설정을 받지 못했다 (HTTP ${response.status}).`);
  }
  return applyRuntimeOverrides(parseRuntimeConfig(await response.json()), overrides);
}

/**
 * `AbortSignal.timeout()` 을 쓰지 않는다 — Safari 16 부터라 **iOS 15 에서 `TypeError`** 다
 * (`spike/ios/` 가 같은 자리에서 데였다). 데모 기기가 정확히 그 버전이다.
 */
async function fetchWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, CONFIG_TIMEOUT_MS);
  try {
    return await fetch(url, { signal: controller.signal });
  } catch (error) {
    // 중단인지 단절인지 화면이 구분할 수 있게 문장을 바꾼다. 「서버가 응답하지 않는다」와
    // 「연결할 수 없다」는 사람이 할 일이 다르다 — 앞엣것은 기다렸다 다시, 뒤엣것은 망이다.
    if (controller.signal.aborted) {
      throw new Error(`서버가 ${CONFIG_TIMEOUT_MS / 1000}초 안에 응답하지 않았다.`, {
        cause: error,
      });
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
